/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
'use client';

import '@/ux/features/website-assembler/products/product-grid-organic.css';

import { Heart, Loader2, MessageCircle, Package, Star } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { metadataCache } from '@/core/ambient-intelligence/utils/MetadataCache';
import { getMetaData } from '@/core/apis/bentoboard-apis';
import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';

import { useOpenMessage } from '../../hooks/useOpenMessage';
import { ContentItemProps } from '../registries/ContentRegistry';

interface ProductMetadata {
  title?: string;
  description?: string;
  price?: number;
  currency?: string;
  images?: string[];
  vendor?: {
    name?: string;
    logo?: string;
  };
  availability?: {
    inStock?: boolean;
    quantity?: number;
  };
}

const ProductCardEnhanced: React.FC<ContentItemProps> = ({
  content,
  variant = 'card',
  onAction,
  className,
}) => {
  const [metadata, setMetadata] = useState<ProductMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { openMessage } = useOpenMessage();

  const isMinimal = variant === 'minimal';
  const isExpanded = variant === 'expanded';
  const isRealProduct = content.data?.isRealProduct || content.data?.isFromWebsite;

  // Fetch metadata for real products
  useEffect(() => {
    if (isRealProduct && content.data?.productUrl) {
      const productUrl = content.data.productUrl;
      const cacheKey = `product_metadata_${content.id}`;

      // Check cache first
      const cachedData = metadataCache.get(cacheKey);
      if (cachedData) {
        setMetadata(cachedData);
        return;
      }

      setLoading(true);
      setError(null);

      // Construct full URL if needed
      const fullUrl = productUrl.startsWith('http') ? productUrl : ``;

      // Fetch metadata using getMetaData API
      getMetaData(null, fullUrl)
        .then((data) => {
          if (data) {
            const metadata = {
              title: data.title || content.title,
              description: data.description || content.description,
              price: data.price,
              currency: data.currencyCode || 'USD',
              images: data.previewImage ? [appendImageRepoDomainIfNotExistInUrl(data.previewImage)] : [],
              vendor: {
                name: data.vendorName,
                logo: data.vendorLogo ? appendImageRepoDomainIfNotExistInUrl(data.vendorLogo) : undefined,
              },
              availability: {
                inStock: data.inStock !== false,
                quantity: data.stockCount,
              },
            };
            setMetadata(metadata);
            // Cache the result
            metadataCache.set(cacheKey, metadata);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to fetch product metadata:', err);
          setError('Failed to load product details');
          setLoading(false);
        });
    }
  }, [isRealProduct, content.data?.productUrl, content.id, content.title, content.description]);

  const handleClick = () => {
    onAction?.('inspect', content);
  };
  // Use metadata if available, otherwise fall back to content data
  const displayTitle = metadata?.title || content.title || content.data?.title || 'Untitled Product';
  const displayDescription =
    metadata?.description || content.description || content.data?.description || 'No description available.';
  const price = metadata?.price || content.data?.price || 0;
  const currency = metadata?.currency || content.data?.currencyCode || 'NOK';
  const inStock = metadata?.availability?.inStock ?? content.data?.inStock !== false;
  const stockCount = metadata?.availability?.quantity || content.data?.stockCount;
  const rating = content.engagement?.rating || 0;
  const imageUrl =
    metadata?.images?.[0] ||
    content.media?.thumbnail?.url ||
    appendImageRepoDomainIfNotExistInUrl(content.data?.previewImage);
  if (isMinimal) {
    return (
      <div
        className={`inspect-theme-bg-elevated rounded-lg p-3 cursor-pointer hover:inspect-theme-shadow transition-all ${className}`}
        onClick={handleClick}
      >
        <div className="flex gap-3">
          {imageUrl && (
            <div className="relative w-16 h-16">
              <img src={imageUrl} alt={displayTitle} className="w-full h-full object-cover rounded" />
              {loading && (
                <div className="absolute inset-0 bg-black/50 rounded flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                </div>
              )}
            </div>
          )}
          <div className="flex-1">
            <h4 className="inspect-theme-text font-medium text-sm line-clamp-1">{displayTitle}</h4>
            {isRealProduct && metadata?.vendor?.name && (
              <p className="text-xs inspect-theme-text-muted">{metadata.vendor.name}</p>
            )}
            <div className="mt-1">
              {price <= 0 ? (
                <span className="free-product-badge">
                  <span className="free-text">Free</span>
                </span>
              ) : (
                <p className="inspect-theme-primary font-semibold">
                  {currency === 'USD' ? '$' : currency === 'NOK' ? `${currency} ` : ''}
                  {price.toFixed(2)}
                </p>
              )}
            </div>
            {rating > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                <span className="text-xs inspect-theme-text-muted">{rating.toFixed(1)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <article
      className={`inspect-theme-bg-elevated rounded-xl overflow-hidden cursor-pointer hover:inspect-theme-shadow-lg transition-all duration-300 ${className}`}
      onClick={handleClick}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        {imageUrl && (
          <img
            src={imageUrl}
            alt={displayTitle}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        )}

        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        )}

        {/* Product Type Badge for Real Products */}
        {isRealProduct && content.data?.productType && (
          <div className="absolute top-2 left-2 inspect-theme-primary-bg text-white px-2 py-1 rounded text-xs font-medium capitalize">
            {content.data.productType}
          </div>
        )}

        {/* Stock Badge */}
        {!inStock && (
          <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
            Out of Stock
          </div>
        )}

        {/* Discount Badge */}
        {content.data?.discount && (
          <div className="absolute bottom-2 left-2 inspect-theme-accent bg-opacity-90 text-white px-2 py-1 rounded text-xs font-medium">
            -{content.data.discount}%
          </div>
        )}
      </div>

      {/* Content */}
      <div className={`p-5 ${isExpanded ? 'space-y-4' : 'space-y-3'}`}>
        {/* Category or Vendor */}
        {(metadata?.vendor?.name || content.categories?.[0]) && (
          <p className="text-xs inspect-theme-text-muted uppercase tracking-wide">
            {metadata?.vendor?.name || content.categories[0]}
          </p>
        )}

        {/* Title */}
        <h3 className={`font-semibold inspect-theme-text ${isExpanded ? 'text-xl' : 'text-lg'} line-clamp-2`}>
          {displayTitle}
        </h3>

        {/* Price & Rating */}
        <div className="flex items-center justify-between">
          <div>
            {price <= 0 ? (
              <div className="free-product-badge mb-1">
                <span className="free-text">Free</span>
              </div>
            ) : (
              <p className="inspect-theme-primary text-2xl font-bold">
                {currency === 'USD' ? '$' : currency === 'NOK' ? `${currency} ` : ''}
                {price.toFixed(2)}
              </p>
            )}
            {content.data?.originalPrice && (
              <div className="flex gap-x-2">
                <p className="text-sm inspect-theme-text-muted line-through">
                  {currency === 'USD' ? '$' : currency === 'NOK' ? `${currency} ` : ''}
                  {content.data.originalPrice.toFixed(2)}
                </p>
                <p>{`(${content.data.discountPercentage || 0}% off)`}</p>
              </div>
            )}
          </div>

          {rating > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < Math.floor(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm inspect-theme-text-muted">({content.engagement?.reviews || 0})</span>
            </div>
          )}
        </div>

        {/* Description */}
        {isExpanded && displayDescription && (
          <p className="inspect-theme-text-muted text-sm line-clamp-3">{displayDescription}</p>
        )}

        {/* Features */}
        {isExpanded && content.data?.features && (
          <ul className="space-y-1 text-sm inspect-theme-text-muted">
            {content.data.features.slice(0, 3).map((feature: string, index: number) => (
              <li key={index} className="flex items-start gap-2">
                <span className="inspect-theme-primary mt-0.5">•</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Stock Info */}
        <div className="flex items-center gap-2 text-sm">
          <Package className={`w-4 h-4 ${inStock ? 'inspect-theme-primary' : 'text-red-500'}`} />
          <span className={inStock ? 'inspect-theme-text' : 'text-red-500'}>
            {inStock ? (stockCount ? `${stockCount} in stock` : 'In stock') : 'Out of stock'}
          </span>
        </div>

        {/* Error message */}
        {error && <p className="text-sm text-red-500">{error}</p>}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAction?.('view_details', content);
            }}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium inspect-theme-button-primary transition-colors"
          >
            View Details
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAction?.('save', content);
            }}
            className="p-2 inspect-theme-button-secondary rounded-lg transition-colors"
            title="Save"
          >
            <Heart className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const vendorId = content.data?.vendor?._id;
              const vendorName = content.data?.vendor?.businessName;
              openMessage({
                vendorId,
                vendorName,
                prefilledMessage: `Hi, I'm interested in learning more about "${displayTitle}"`,
                contentContext: content,
              });
            }}
            className="p-2 inspect-theme-button-secondary rounded-lg transition-colors"
            title="Chat about this product"
          >
            <MessageCircle className="w-4 h-4" />
          </button>
        </div>
      </div>
    </article>
  );
};

// Export without auto-registration to avoid conflicts
export default ProductCardEnhanced;
